import {
  type ReactNode,
  type ReactElement,
  Children,
  isValidElement,
} from 'react';

import { AreaSeries } from '../slots/AreaSeries.js';
import { BarSeries } from '../slots/BarSeries.js';
import { DotSeries } from '../slots/DotSeries.js';
import { LineSeries } from '../slots/LineSeries.js';
import { RectSeries } from '../slots/RectSeries.js';
import type { XYChartData } from './../types/xy-chart.js';

/**
 * Check if there is a Series slot as a children component and if their
 * data props is empty or not to display Series as a priority against the root
 * data props
 * @param children - A children as a ReactElement type
 * @returns boolean
 **/
export const typesOfCharts = [
  RectSeries.displayName,
  DotSeries.displayName,
  LineSeries.displayName,
  BarSeries.displayName,
  AreaSeries.displayName,
];

type ElementWithDisplayName = { type: { displayName?: string } };
type PropsWithData = { data?: XYChartData };

function isSeriesSlot(
  el: ReactNode,
): el is ReactElement<PropsWithData> & ElementWithDisplayName {
  return isValidElement<PropsWithData>(el);
}

function isXYSeriesSlot(el: ElementWithDisplayName) {
  return typesOfCharts.includes(el.type.displayName || '');
}

export const isEmptyData = (
  children: ReactNode | undefined,
  data: XYChartData[],
) => {
  if (data.length) {
    return false;
  }
  const anySeriesHasData = Children.toArray(children).some((el) => {
    return isSeriesSlot(el) && isXYSeriesSlot(el) && el.props.data?.length;
  });

  return !anySeriesHasData;
};
